// Per-day fixture builders for the Revenue Calendar day-detail popup.
// All values derive deterministically from the date string so the same day
// always shows the same breakdown — keeps demos predictable.

import { DiscountPlatform, PlatformDiscountStatus } from 'src/shared/services/listing-calendar.service';

// Layer 1 isn't a price-modifying layer — it's the bounds/override that
// constrains everything below it.
export interface BoundsLayer {
  minCents: number;
  maxCents: number;
  overrideCents: number | null;
  blockedOff: boolean;
}

// Bounds from the property's Booking Settings (mocked).
export const PROPERTY_BOUNDS = {
  minCents: 18900,
  maxCents: 46000,
  // Default stay-length rules — also overridable per-date in Layer 1.
  defaultMinNights: 2,
  defaultMaxNights: 28,
};

export type PaceState = 'ahead' | 'on-track' | 'behind' | 'strong-demand';

export const PACE_STATE_LABELS: Record<PaceState, string> = {
  ahead: 'Ahead of target',
  'on-track': 'On track',
  behind: 'Behind target',
  'strong-demand': 'Strong demand',
};

export interface RankAdjustment {
  pct: number;
  /** AI-style sentence explaining the adjustment, picked from a small template pool. */
  narrative: string;
  fromRank: number | null;
  paceState: PaceState;
  daysOut: number;
}

export interface PerPlatformPrice {
  platform: 'airbnb' | 'vrbo' | 'booking';
  label: string;
  finalCents: number;
  discountPct: number;
  reason: string;
  /**
   * Reconciliation state for real accounts. Mock rows are always 'configured'
   * (nothing to reconcile) so the demo pipeline renders no sync affordance.
   */
  status: PlatformDiscountStatus;
}

export type ListingChangeKind = 'photo' | 'title' | 'description' | 'amenity';

export interface ListingChange {
  kind: ListingChangeKind;
  summary: string;
  whenLabel: string;
}

export interface ListingOptimization {
  changes: ListingChange[];
}

export interface RankCell {
  query: string;
  platform: 'airbnb' | 'vrbo' | 'booking';
  position: number | null;
  page: number | null;
}

export interface DayLever {
  type: 'discount' | 'last-minute' | 'orphan-night' | 'promoted' | 'min-stay';
  label: string;
  detail: string;
  since: string;
}

export interface TrackedQuery {
  id: string;
  label: string;
  location: string;
  adults: number;
  nights: number;
  leadTimeDays: number;
  enabled: boolean;
}

export const DEFAULT_TRACKED_QUERIES: TrackedQuery[] = [
  { id: 'q1', label: 'Costa Mesa · 2 guests · 14d out · 3 nights', location: 'Costa Mesa, CA', adults: 2, nights: 3, leadTimeDays: 14, enabled: true },
  { id: 'q2', label: 'Costa Mesa · 4 guests · 30d out · 3 nights', location: 'Costa Mesa, CA', adults: 4, nights: 3, leadTimeDays: 30, enabled: true },
  { id: 'q3', label: 'Newport Beach · 2 guests · 14d out · 3 nights', location: 'Newport Beach, CA', adults: 2, nights: 3, leadTimeDays: 14, enabled: true },
  { id: 'q4', label: 'Costa Mesa · 6 guests · 60d out · 5 nights', location: 'Costa Mesa, CA', adults: 6, nights: 5, leadTimeDays: 60, enabled: false },
];

export type Platform = 'airbnb' | 'vrbo' | 'booking';

export const PLATFORM_LABELS: Record<Platform, string> = {
  airbnb: 'Airbnb',
  vrbo: 'Vrbo',
  booking: 'Booking.com',
};

// Platforms we currently scrape rank for. Booking.com is intentionally
// excluded until the backend scraping reliability story is figured out for it.
export const ACTIVE_PLATFORMS: Platform[] = ['airbnb', 'vrbo'];

// Platforms we publish to (and can therefore apply per-platform discounts on),
// regardless of whether we track rank for them.
export const DISCOUNT_PLATFORMS: Platform[] = ['airbnb', 'vrbo', 'booking'];

function dateSeed(date: string): number {
  // Simple deterministic seed from yyyy-mm-dd.
  let s = 0;
  for (let i = 0; i < date.length; i++) {s = (s * 31 + date.charCodeAt(i)) & 0xffff;}
  return s;
}

function rng(seed: number): () => number {
  let s = seed || 1;
  return () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
}

// Layer 2: PriceLabs suggested. Their algorithmic output for this date.
// We treat the calendar's `price` as a reasonable proxy for PriceLabs.
export function buildPriceLabsSuggested(date: string, baseCents: number): { cents: number; model: string; deltaFromMidpointPct: number } {
  const d = new Date(`${date  }T00:00:00`);
  const isWeekend = d.getDay() === 5 || d.getDay() === 6;
  const midpoint = (PROPERTY_BOUNDS.minCents + PROPERTY_BOUNDS.maxCents) / 2;
  const deltaPct = ((baseCents - midpoint) / midpoint) * 100;
  return {
    cents: baseCents,
    model: isWeekend ? 'Weekend demand model' : 'Weekday demand model',
    deltaFromMidpointPct: Math.round(deltaPct),
  };
}

// Pace state derived deterministically from the date — pretend our backend
// tells us where this date sits vs target booking pace.
export function buildPaceState(date: string): PaceState {
  const r = rng(dateSeed(date) + 101)();
  if (r < 0.2) {return 'strong-demand';}
  if (r < 0.4) {return 'ahead';}
  if (r < 0.75) {return 'on-track';}
  return 'behind';
}

type AdjustmentBucket =
  | 'top5-strong'
  | 'top5-flat'
  | 'last-minute'
  | 'gap-night'
  | 'behind-pace'
  | 'far-ahead'
  | 'far-low-rank-strong-demand'
  | 'hold';

// Pool of ~20 narrative templates, grouped by bucket. Picked deterministically
// per date so the same day always reads the same way.
const NARRATIVE_TEMPLATES: Record<AdjustmentBucket, string[]> = {
  'top5-strong': [
    'Already in the top five with demand running ahead — taking a small step up to find the price ceiling.',
    'Visibility is strong and bookings are tracking — quietly lifting the rate to capture more per night.',
    'Top-five rank with healthy pace is a green light to nudge price upward.',
  ],
  'top5-flat': [
    'Top-five visibility with no bookings usually points at photos, copy, or friction — holding price flat is the right move.',
    'Rank is fine but the page isn\'t converting. Pricing isn\'t the lever; the listing content probably is.',
    'You\'re in the top five and still empty — leaving price alone and flagging listing content as the likely fix.',
  ],
  'last-minute': [
    'This date is right around the corner and your nearest competitors are filling up — pulling pricing down to win the last-minute booker.',
    'Inside the seven-day window and the calendar\'s still open — recommending a sharper price cut to close it.',
    'Under a week out with nothing booked; an aggressive discount is the highest-conversion lever we have here.',
  ],
  'gap-night': [
    'Gap nights this close to check-in rarely fill at full price; trimming the rate gives this date a stronger shot in last-minute search.',
    'Your rank for this night sits past the first page and the booking window is tightening — softening the price to chase the click-through.',
    'A weak rank plus a short runway typically warrants a real discount, not a small one.',
  ],
  'behind-pace': [
    'Booking pace is trailing target and your rank for this date isn\'t competitive — a modest cut keeps this night moving.',
    'Demand signals are mild and your rank sits below the top twenty; a slightly softer price should reopen consideration.',
    'Pace is behind and visibility is mid-pack — a small adjustment downward should restart the conversation.',
  ],
  'far-ahead': [
    'Demand is running ahead of target on this date with little room to spare — quietly lifting price to capture more revenue per night.',
    'Pacing strong this far out is usually a sign you can pull the lever upward without losing the booking.',
    'Forward demand looks confident; raising the rate while the market supports it.',
  ],
  'far-low-rank-strong-demand': [
    'Demand for the broader window is strong but your rank is dragging — a small decrease should widen visibility without leaving real money behind.',
    'Strong market demand and weak rank is the kind of mismatch a slight discount usually fixes.',
  ],
  hold: [
    'Signals are stable; no adjustment beyond the suggested price is warranted today.',
    'Nothing in the data flags a meaningful change here — letting the suggested price ride.',
    'No condition strong enough to override the suggested price. Holding.',
  ],
};

function pickNarrative(bucket: AdjustmentBucket, seed: number): string {
  const pool = NARRATIVE_TEMPLATES[bucket];
  return pool[Math.floor(rng(seed)() * pool.length) % pool.length];
}

// Layer 3: Rank Tracker adjustment. Picks an adjustment % by matching the
// (daysOut, paceState, bestRank) tuple against the rule table, then surfaces
// a deterministic AI-styled narrative from the template pool.
export function buildRankAdjustment(date: string, rankCells: RankCell[], pace: PaceState): RankAdjustment {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(`${date  }T00:00:00`);
  const daysOut = Math.max(0, Math.round((d.getTime() - today.getTime()) / 86400000));
  const positions = rankCells.map(c => c.position).filter((p): p is number => p !== null);
  const bestRank = positions.length ? Math.min(...positions) : null;
  const seed = dateSeed(date) + 199;

  let pct = 0;
  let bucket: AdjustmentBucket = 'hold';

  if (bestRank !== null && bestRank <= 5) {
    if (pace === 'strong-demand' || pace === 'ahead') { pct = 6;  bucket = 'top5-strong'; }
    else                                              { pct = 0;  bucket = 'top5-flat'; }
  } else if (daysOut <= 7) {
    pct = -22; bucket = 'last-minute';
  } else if (daysOut <= 14 && (bestRank === null || bestRank > 30)) {
    pct = -15; bucket = 'gap-night';
  } else if (daysOut <= 30 && pace === 'behind' && (bestRank === null || bestRank > 20)) {
    pct = -8; bucket = 'behind-pace';
  } else if (daysOut > 30 && pace === 'ahead') {
    pct = 12; bucket = 'far-ahead';
  } else if (daysOut > 30 && pace === 'strong-demand' && bestRank !== null && bestRank > 30) {
    pct = -3; bucket = 'far-low-rank-strong-demand';
  }

  return {
    pct,
    narrative: pickNarrative(bucket, seed),
    fromRank: bestRank,
    paceState: pace,
    daysOut,
  };
}

// Clamp a price to the configured bounds.
export function clampToBounds(cents: number, bounds: { minCents: number; maxCents: number }): number {
  return Math.max(bounds.minCents, Math.min(bounds.maxCents, cents));
}

/** API slug → the mock/UI platform id, which predates the `booking_com` slug. */
export const PLATFORM_BY_SLUG: Record<DiscountPlatform, Platform> = {
  airbnb: 'airbnb',
  vrbo: 'vrbo',
  booking_com: 'booking',
};

/**
 * The lever that won a date, in the page's wording. Only the two
 * booking-window levers can win a per-date discount.
 */
export const DISCOUNT_REASON_LABELS: Record<'far_out' | 'last_minute', string> = {
  far_out: 'Far-out booking',
  last_minute: 'Last-minute booking',
};

const ALL_PLATFORM_PRICE_BUILDERS: Record<Platform, (r: () => number, subtotalCents: number) => PerPlatformPrice> = {
  airbnb: (r, sub) => {
    const d = r() < 0.35 ? 5 : 0;
    return {
      platform: 'airbnb', label: 'Airbnb',
      finalCents: Math.round(sub * (1 - d / 100)),
      discountPct: d, reason: d ? 'Promoted listing' : 'No promo',
      status: 'configured',
    };
  },
  vrbo: (r, sub) => {
    const d = r() < 0.18 ? 4 : 0;
    return {
      platform: 'vrbo', label: 'Vrbo',
      finalCents: Math.round(sub * (1 - d / 100)),
      discountPct: d, reason: d ? 'Channel co-op' : 'No promo',
      status: 'configured',
    };
  },
  booking: (r, sub) => {
    const d = r() < 0.5 ? 3 : 0;
    return {
      platform: 'booking', label: 'Booking.com',
      finalCents: Math.round(sub * (1 - d / 100)),
      discountPct: d, reason: d ? 'Genius member discount' : 'No promo',
      status: 'configured',
    };
  },
};

export function buildPlatformPrices(date: string, subtotalCents: number): PerPlatformPrice[] {
  const r = rng(dateSeed(date) + 7);
  return DISCOUNT_PLATFORMS.map(p => ALL_PLATFORM_PRICE_BUILDERS[p](r, subtotalCents));
}

export function buildListingOptimization(date: string): ListingOptimization {
  const r = rng(dateSeed(date) + 13);

  // Pool of possible changes — shuffled deterministically per date so the
  // same date always shows the same history.
  const pool: { kind: ListingChangeKind; summary: string }[] = [
    { kind: 'photo',       summary: 'New hero photo — twilight hot tub' },
    { kind: 'title',       summary: 'Title now leads with "Hot Tub Beachside"' },
    { kind: 'description', summary: 'Added "5-min walk to beach" to first line' },
    { kind: 'amenity',     summary: 'Added EV charger to amenity list' },
    { kind: 'photo',       summary: 'Reordered bedroom photos — primary first' },
    { kind: 'description', summary: 'Rewrote bullets to emphasize quiet street' },
    { kind: 'amenity',     summary: 'Added high-speed Wi-Fi badge (210 Mbps)' },
    { kind: 'title',       summary: 'Trimmed title length 76 → 48 chars' },
    { kind: 'photo',       summary: 'New living room photo — daylight' },
    { kind: 'description', summary: 'Added "pet friendly" callout in summary' },
  ];

  const shuffled = [...pool].sort(() => r() - 0.5);
  const count = 4 + Math.floor(r() * 3); // 4–6 entries
  let cursor = 1;
  const changes: ListingChange[] = shuffled.slice(0, count).map(entry => {
    const days = cursor + Math.floor(r() * 6);
    cursor = days + 2;
    const whenLabel = days < 7
      ? (days <= 1 ? 'Yesterday' : `${days} days ago`)
      : days < 30
        ? `${Math.floor(days / 7)} week${Math.floor(days / 7) === 1 ? '' : 's'} ago`
        : `${Math.floor(days / 30)} month${Math.floor(days / 30) === 1 ? '' : 's'} ago`;
    return { ...entry, whenLabel };
  });

  return { changes };
}

export function buildRankBreakdown(date: string, queries: TrackedQuery[]): RankCell[] {
  const r = rng(dateSeed(date) + 23);
  const cells: RankCell[] = [];
  for (const q of queries) {
    if (!q.enabled) {continue;}
    for (const p of ACTIVE_PLATFORMS) {
      const base = (new Date(`${date  }T00:00:00`).getDay() === 5 || new Date(`${date  }T00:00:00`).getDay() === 6) ? 7 : 16;
      const platformOffset = p === 'airbnb' ? 0 : p === 'vrbo' ? 5 : 10;
      const noise = Math.floor(r() * 16);
      const position = Math.min(99, Math.max(1, base + platformOffset + noise - 4));
      cells.push({
        query: q.id,
        platform: p,
        position: r() < 0.08 ? null : position,
        page: position ? Math.ceil(position / 20) : null,
      });
    }
  }
  return cells;
}

export function buildLevers(date: string): DayLever[] {
  const r = rng(dateSeed(date) + 41);
  const out: DayLever[] = [];
  if (r() < 0.4) {out.push({ type: 'discount',    label: 'Weekly stay discount 10%', detail: 'Auto-applied to 7+ night stays', since: '2 weeks ago' });}
  if (r() < 0.3) {out.push({ type: 'last-minute', label: 'Last-minute 15% off',      detail: '7-day check-in window',          since: '4 days ago' });}
  if (r() < 0.18) {out.push({ type: 'orphan-night', label: 'Orphan-night fill',     detail: 'Min-stay relaxed to 1 night',     since: 'Today' });}
  if (r() < 0.2) {out.push({ type: 'promoted',    label: 'Promoted listing (Airbnb)', detail: '4% bid',                        since: '6 days ago' });}
  return out;
}

export function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

// ---------- Layer 5: Minimum-stay / length-of-stay optimization ----------
// Each rule is one concrete instance of the "100+ rules" library. The picker
// in the UI lets you preview different models. The curve is % vs the target
// per-night rate: positive = premium charged for short stays (to cover
// turnover), negative = discount for longer stays (to drive occupancy).

export interface StayCurvePoint {
  nights: number;
  pctVsTarget: number;
}

export interface StayRule {
  id: string;
  name: string;
  description: string;
  minNights: number;
  maxNights: number;
  curve: StayCurvePoint[];
}

// Shared tier set so every curve lines up on the same X-axis.
export const STAY_CURVE_TIERS = [1, 2, 3, 4, 5, 6, 7, 14, 28];

function curve(values: number[]): StayCurvePoint[] {
  return STAY_CURVE_TIERS.map((n, i) => ({ nights: n, pctVsTarget: values[i] }));
}

export const STAY_RULES: StayRule[] = [
  {
    id: 'wide-open-far-out',
    name: 'Wide open · far out',
    description: 'The calendar is wide open and this date is far out. Holding a 3-night minimum and pricing short stays at a premium so a 1–2 night booking doesn’t fragment a bookable week.',
    minNights: 3,
    maxNights: 28,
    curve: curve([28, 20, 13, 7, 2, -1, -5, -10, -15]),
  },
  {
    id: 'weekend-protection',
    name: 'Weekend protection',
    description: 'This weekend night is far enough out to protect. A 3-night minimum keeps a Saturday-only guest from stranding your Friday — we’ll relax it as the date approaches if nothing lands.',
    minNights: 3,
    maxNights: 21,
    curve: curve([30, 24, 10, 5, 0, -3, -6, -10, -14]),
  },
  {
    id: 'orphan-gap-fill',
    name: 'Orphan gap fill · close in',
    description: 'A short gap trapped between two bookings, and the date is close. Dropped the minimum to the gap length and trimmed the short-stay premium — an empty night earns nothing, so filling it wins.',
    minNights: 2,
    maxNights: 4,
    curve: curve([12, 6, 2, 0, -2, -4, -6, -10, -13]),
  },
  {
    id: 'single-orphan',
    name: 'Single orphan night',
    description: 'A single stranded night between reservations. We allow a 1-night booking but price it to recover the full turnover cost, since the premium is spread over just one night.',
    minNights: 1,
    maxNights: 1,
    curve: curve([40, 30, 22, 16, 12, 9, 6, 2, 0]),
  },
  {
    id: 'last-minute-empty',
    name: 'Last-minute · still empty',
    description: 'Days away and still open. Minimum dropped to 1 and the curve flattened — at this point almost anything that books beats an empty night.',
    minNights: 1,
    maxNights: 14,
    curve: curve([5, 2, 0, -2, -4, -6, -9, -14, -18]),
  },
  {
    id: 'peak-demand',
    name: 'Peak / high-demand',
    description: 'A high-demand window. Requiring longer stays and barely discounting — demand supports holding out for a strong, longer booking rather than giving nights away.',
    minNights: 4,
    maxNights: 21,
    curve: curve([22, 16, 10, 4, 1, 0, -2, -4, -7]),
  },
  {
    id: 'monthly-capture',
    name: 'Monthly-stay capture',
    description: 'Targeting a 28+ night booking for this window. Short stays carry a premium while the monthly rate drops sharply to pull in a long, low-turnover guest.',
    minNights: 2,
    maxNights: 60,
    curve: curve([18, 12, 8, 4, 2, 0, -6, -16, -28]),
  },
  {
    id: 'shoulder-balanced',
    name: 'Shoulder · balanced',
    description: 'A normal shoulder-season date. Balanced 2-night minimum with a gentle premium on short stays and standard weekly/monthly discounts.',
    minNights: 2,
    maxNights: 28,
    curve: curve([18, 12, 7, 3, 0, -2, -5, -9, -13]),
  },
];

// Deterministic default rule for a date — biased by day-of-week and lead time
// so the preselected model feels plausible before the user flips the picker.
export function pickDefaultStayRule(date: string): StayRule {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(`${date  }T00:00:00`);
  const daysOut = Math.max(0, Math.round((d.getTime() - today.getTime()) / 86400000));
  const dow = d.getDay();
  const isWeekend = dow === 5 || dow === 6;

  let id: string;
  if (daysOut <= 7) {id = 'last-minute-empty';}
  else if (isWeekend && daysOut > 21) {id = 'weekend-protection';}
  else {
    const roll = rng(dateSeed(date) + 71)();
    if (roll < 0.22) {id = 'wide-open-far-out';}
    else if (roll < 0.40) {id = 'orphan-gap-fill';}
    else if (roll < 0.55) {id = 'single-orphan';}
    else if (roll < 0.72) {id = 'peak-demand';}
    else if (roll < 0.85) {id = 'monthly-capture';}
    else {id = 'shoulder-balanced';}
  }
  return STAY_RULES.find(r => r.id === id) ?? STAY_RULES[0];
}

// ---------- Surrounding booking window (drives the min/max decision) ----------
// The actual input to the stay-rule engine: the booked/open pattern of nights
// around this date. The contiguous open gap containing the date determines how
// long a guest could stay and whether a short booking would strand a night.

export interface WindowNight {
  date: string;
  dayNum: number;
  dowLabel: string;
  booked: boolean;
  isSelected: boolean;
  inGap: boolean;
  glow: number;          // 0 at the selected date → 1 at the open night nearest a booking
}

export interface SurroundingWindow {
  nights: WindowNight[];      // nearest booking → open gap → nearest booking
  gapLength: number;          // contiguous open nights containing the selected date
  maxFromSelected: number;    // open nights from the selected date to the next booking
  daysOut: number;
  openLeft: boolean;          // gap runs open past the display edge (no near booking)
  openRight: boolean;
  suggestedMinNights: number;
  suggestedMaxNights: number;
  narrative: string;
}

const DOW2 = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function isoLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

export function buildSurroundingWindow(dateStr: string, selectedAvailable: boolean): SurroundingWindow {
  const sel = new Date(`${dateStr  }T00:00:00`);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const daysOut = Math.max(0, Math.round((sel.getTime() - today.getTime()) / 86400000));

  // Generate a wide underlying calendar so we can find the real nearest
  // bookings on each side. Clustered via a run-walk so nights group into
  // reservation-like blocks. Index `origin` is the selected date.
  const SCAN = 30;                 // scan ±30 nights for the nearest booking
  const MAX_OPEN_SIDE = 11;        // cap open nights shown per side if no near booking
  const total = SCAN * 2 + 1;
  const origin = SCAN;
  const flags: boolean[] = [];
  let booked = rng(dateSeed(dateStr) + 301)() < 0.5;
  let runLeft = 0;
  for (let i = 0; i < total; i++) {
    if (runLeft <= 0) {
      booked = rng(dateSeed(dateStr) + 311 + i)() < 0.5;
      runLeft = 2 + Math.floor(rng(dateSeed(dateStr) + 317 + i)() * 3); // runs of 2–4
    }
    flags.push(booked);
    runLeft--;
  }
  flags[origin] = !selectedAvailable; // selected night reflects its real availability

  // Open gap containing the selected date.
  let gapStart = origin, gapEnd = origin;
  if (!flags[origin]) {
    while (gapStart - 1 >= 0 && !flags[gapStart - 1]) {gapStart--;}
    while (gapEnd + 1 < total && !flags[gapEnd + 1]) {gapEnd++;}
  }
  const gapLength = flags[origin] ? 0 : (gapEnd - gapStart + 1);
  const maxFromSelected = flags[origin] ? 0 : (gapEnd - origin + 1);

  // Window = the booked night just before the gap → gap → the booked night just
  // after. If the gap runs open past the scan/cap, mark that side open-ended.
  const leftBooked = gapStart - 1 >= 0 && flags[gapStart - 1];
  const rightBooked = gapEnd + 1 < total && flags[gapEnd + 1];

  let winStart = leftBooked ? gapStart - 1 : gapStart;
  let winEnd = rightBooked ? gapEnd + 1 : gapEnd;

  let openLeft = !leftBooked;
  let openRight = !rightBooked;
  if (origin - winStart > MAX_OPEN_SIDE) { winStart = origin - MAX_OPEN_SIDE; openLeft = true; }
  if (winEnd - origin > MAX_OPEN_SIDE) { winEnd = origin + MAX_OPEN_SIDE; openRight = true; }

  const nights: WindowNight[] = [];
  for (let i = winStart; i <= winEnd; i++) {
    const d = new Date(sel);
    d.setDate(sel.getDate() + (i - origin));
    nights.push({
      date: isoLocal(d),
      dayNum: d.getDate(),
      dowLabel: DOW2[d.getDay()],
      booked: flags[i],
      isSelected: i === origin,
      inGap: !flags[origin] && i >= gapStart && i <= gapEnd,
      glow: 0,
    });
  }

  // Glow ramps from 0 at the selected date to 1 at the open night nearest a
  // booking on each side — drawing the eye to the edges of the fillable gap.
  const selLocal = nights.findIndex(n => n.isSelected);
  let firstOpen = selLocal, lastOpen = selLocal;
  while (firstOpen - 1 >= 0 && nights[firstOpen - 1].inGap) {firstOpen--;}
  while (lastOpen + 1 < nights.length && nights[lastOpen + 1].inGap) {lastOpen++;}
  for (let j = 0; j < nights.length; j++) {
    if (!nights[j].inGap || j === selLocal) {continue;}
    nights[j].glow = j < selLocal
      ? (selLocal - j) / Math.max(1, selLocal - firstOpen)
      : (j - selLocal) / Math.max(1, lastOpen - selLocal);
  }

  // Derive min/max from the gap + lead time.
  const preferredMin = 3;
  let suggestedMinNights: number;
  if (gapLength <= 1) {suggestedMinNights = daysOut <= 14 ? 1 : preferredMin;}
  else if (gapLength < preferredMin) {suggestedMinNights = daysOut <= 14 ? gapLength : preferredMin;}
  else {suggestedMinNights = preferredMin;}
  // No real booking after the gap → max-stay is effectively open.
  const suggestedMaxNights = gapLength === 0 || openRight ? 28 : Math.min(28, maxFromSelected);

  return {
    nights,
    gapLength,
    maxFromSelected,
    daysOut,
    openLeft,
    openRight,
    suggestedMinNights,
    suggestedMaxNights,
    narrative: buildWindowNarrative(gapLength, maxFromSelected, daysOut, suggestedMinNights, suggestedMaxNights, openRight),
  };
}

function buildWindowNarrative(gapLength: number, maxFromSelected: number, daysOut: number, minN: number, maxN: number, openRight: boolean): string {
  if (gapLength === 0) {return 'This night is already booked.';}
  if (gapLength === 1) {
    return daysOut <= 14
      ? 'A single open night wedged between two reservations. We drop the minimum to 1 so it can book at all, and lean on a short-stay premium to cover the turnover.'
      : `A single open night, but it's ${daysOut} days out — we hold a ${minN}-night minimum for now and relax it toward the date if nothing lands.`;
  }
  if (openRight) {
    return `An open run of ${gapLength}+ nights with no near booking after this date. We hold a ${minN}-night minimum to avoid fragmenting a bookable week; with nothing capping it, max-stay stays open.`;
  }
  // Bounded gap between two reservations.
  return daysOut <= 14
    ? `This date sits in a ${gapLength}-night gap between two reservations. A stay from here can run at most ${maxFromSelected} nights, so we cap max-stay at ${maxN} and hold a ${minN}-night minimum to keep the gap fillable.`
    : `A ${gapLength}-night gap between two reservations, still ${daysOut} days out. We hold a ${minN}-night minimum to avoid stranding nights; max-stay is capped at ${maxN} by the next booking.`;
}
